import { type CancelToken } from 'axios';
import type {
  ColumnId, SpreadsheetColumnId,
} from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import type { CombinedRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { notNullsy } from '~/_shared/utils/typeGuards';
import {
  getSpreadsheetBulkData, type SpreadsheetColumnsToFetchResponse, type SpreadsheetDataBulkRequestGetter,
} from '~/spreadsheet/spreadsheet.repository';
import { isSuccessBulkSpreadsheet } from '~/spreadsheet/typeguards';
import { DataType } from '~/store/spreadsheetData/spreadsheetData.state';

type RowValueForDataType<T extends DataType> = NonNullable<SpreadsheetColumnsToFetchResponse[T]>[ColumnId]['row_values'][CombinedRowId];

export type SpreadsheetColumnRowValues<T extends DataType = DataType> =
  ReadonlyMap<CombinedRowId, NonNullable<RowValueForDataType<T>>>;

type GetSpreadsheetColumnRowValuesArguments<T extends DataType> = {
  clientId: number;
  mapId: number;
  cancelToken: CancelToken;
  spreadsheetColumn: SpreadsheetColumnId;
  dataType: T;
};

// Fetches raw per-row values for a single column, with no filter tree or aggregation applied,
// for callers that need to do their own row-scoped aggregation.
export const getSpreadsheetColumnRowValues = async <T extends DataType>({
  clientId, mapId, cancelToken, spreadsheetColumn, dataType,
}: GetSpreadsheetColumnRowValuesArguments<T>): Promise<SpreadsheetColumnRowValues<T>> => {
  const params: SpreadsheetDataBulkRequestGetter[] = [{
    map_id: mapId,
    spreadsheet_id: spreadsheetColumn.spreadsheetId,
    exclude_basic_data: true,
    exclude_row_data: false,
    columns_to_fetch: {
      [dataType]: {
        [spreadsheetColumn.columnId]: dataType === DataType.NUMBER ? { empty_is_null: true } : {},
      },
    },
  }];

  const response = await getSpreadsheetBulkData(clientId, { params }, cancelToken);
  const spreadsheet = response.data
    .filter(isSuccessBulkSpreadsheet)
    .find(item => item.spreadsheet_id === spreadsheetColumn.spreadsheetId);
  const columnValues = spreadsheet?.result.columns_to_fetch?.[dataType]?.[spreadsheetColumn.columnId];

  const rowValues = new Map<CombinedRowId, NonNullable<RowValueForDataType<T>>>(
    Object.entries(columnValues?.row_values ?? {})
      .filter((entry) => notNullsy(entry[1])),
  );

  return rowValues;
};
